import React, { useEffect, useState, useRef } from "react";
import "./FormLayout.css";
import "./DateField.css";
import Calendar from "./CustomCalendar";
import Label from "./Label";

export function DateField({ field, value, onChange, onBlur, errorMessage, viewMode, disabled, minDate, maxDate, clearError, openUpward = false }) {
    const [open, setOpen] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [dd, setDd] = useState("");
    const [mm, setMm] = useState("");
    const [yyyy, setYyyy] = useState("");

    const ref = useRef(null);
    const ddRef = useRef(null);
    const mmRef = useRef(null);
    const yyyyRef = useRef(null);
    const justSelectedRef = useRef(false);

    const isDisabled = disabled || field.isDisabled || field.disabled;

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                if (isTyping && dd && mm && yyyy.length === 4) {
                    tryCommitDate(dd, mm, yyyy);
                }
                setOpen(false);
                setIsTyping(false);
                setDd(""); setMm(""); setYyyy("");
                if (clearError) clearError(field.name);
                if (hasInteracted && !justSelectedRef.current && onBlur) {
                    if (value !== null && value !== undefined && value !== "") {
                        onBlur(field.name, value);
                    }
                }
                justSelectedRef.current = false;
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [onBlur, field.name, hasInteracted, value, isTyping, dd, mm, yyyy, clearError]);

    useEffect(() => {
        if (!errorMessage) {
            setHasInteracted(false);
        }
    }, [errorMessage]);

    const formatted = (() => {
        if (value instanceof Date) {
            return `${String(value.getDate()).padStart(2, "0")}/${String(value.getMonth() + 1).padStart(2, "0")}/${value.getFullYear()}`;
        }
        if (typeof value === "string" && value) {
            const d = new Date(value);
            if (!isNaN(d)) {
                return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
            }
        }
        return "";
    })();

    const placeholderText = errorMessage
        ? errorMessage
        : field.placeholder || "DD/MM/YYYY";

    const triggerClass = [
        "date-field-trigger",
        open ? "open" : "",
        errorMessage ? "error" : "",
        isDisabled ? "disabled" : "",
    ].filter(Boolean).join(" ");

    const tryCommitDate = (ddVal, mmVal, yyyyVal) => {
        const ddN = parseInt(ddVal, 10);
        const mmN = parseInt(mmVal, 10);
        const yyyyN = parseInt(yyyyVal, 10);

        if (isNaN(ddN) || isNaN(mmN) || isNaN(yyyyN)) return false;

        const parsed = new Date(yyyyN, mmN - 1, ddN);
        const isValid =
            !isNaN(parsed) &&
            parsed.getDate() === ddN &&
            parsed.getMonth() === mmN - 1 &&
            parsed.getFullYear() === yyyyN;

        if (isValid) {
            justSelectedRef.current = true;
            onChange(field.name, parsed);
            if (clearError) clearError(field.name);
            if (onBlur) onBlur(field.name, parsed);
            setIsTyping(false);
            setDd(""); setMm(""); setYyyy("");
            setOpen(false);
            return true;
        }
        return false;
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(field.name, null);
        if (clearError) clearError(field.name);
        setIsTyping(false);
        setDd(""); setMm(""); setYyyy("");
        setOpen(false);
    };

    const handleTriggerMouseDown = (e) => {
        if (viewMode || isDisabled) return;

        if (isTyping) {
            ddRef.current?.focus();
            return;
        }

        e.preventDefault();
        setHasInteracted(true);
        if (clearError) clearError(field.name);
        setOpen(true);

        if (value) {
            setIsTyping(true);
            const d = value instanceof Date ? value : new Date(value);
            setDd(String(d.getDate()).padStart(2, "0"));
            setMm(String(d.getMonth() + 1).padStart(2, "0"));
            setYyyy(String(d.getFullYear()));
            setTimeout(() => ddRef.current?.focus(), 0);
        } else {
            setIsTyping(true);
            setTimeout(() => ddRef.current?.focus(), 0);
        }
    };

    return (
        <div ref={ref} className="date-field-wrapper">
            <div onClickCapture={(e) => e.preventDefault()}>
                <Label
                    className={"custom-label"}
                    labelText={field.label}
                    isCompulsary={field.required}
                />
            </div>

            <div className={triggerClass} onMouseDown={handleTriggerMouseDown}>
                {isTyping ? (
                    <div className="date-field-parts">
                        <input
                            ref={ddRef}
                            className="date-part-input"
                            type="text"
                            value={dd}
                            maxLength={2}
                            placeholder="DD"
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, "");
                                setDd(val);
                                if (val.length === 2) mmRef.current?.focus();
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Backspace" && dd === "") return;
                            }}
                            onBlur={() => {
                                if (!dd && !mm && !yyyy) setIsTyping(false);
                            }}
                        />
                        <span className="date-part-sep">/</span>
                        <input
                            ref={mmRef}
                            className="date-part-input"
                            type="text"
                            value={mm}
                            maxLength={2}
                            placeholder="MM"
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, "");
                                setMm(val);
                                if (val.length === 2) yyyyRef.current?.focus();
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Backspace" && mm === "") ddRef.current?.focus();
                            }}
                            onBlur={() => {
                                if (!dd && !mm && !yyyy) setIsTyping(false);
                            }}
                        />
                        <span className="date-part-sep">/</span>
                        <input
                            ref={yyyyRef}
                            className="date-part-input date-part-year"
                            type="text"
                            value={yyyy}
                            maxLength={4}
                            placeholder="YYYY"
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, "");
                                setYyyy(val);
                                if (val.length === 4) tryCommitDate(dd, mm, val);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Backspace" && yyyy === "") mmRef.current?.focus();
                            }}
                            onBlur={() => {
                                if (dd && mm && yyyy.length === 4) {
                                    tryCommitDate(dd, mm, yyyy);
                                } else if (!dd && !mm && !yyyy) {
                                    setIsTyping(false);
                                }
                            }}
                        />
                    </div>
                ) : (
                    <>
                        {formatted && !errorMessage ? (
                            <span
                                className="date-field-value"
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleTriggerMouseDown(e);
                                }}
                            >
                                {formatted}
                            </span>
                        ) : (
                            <span
                                className={`date-field-placeholder${errorMessage ? " error" : ""}`}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    handleTriggerMouseDown(e);
                                }}
                            >
                                {placeholderText}
                            </span>
                        )}
                    </>
                )}

                <svg
                    className={`date-field-icon${open ? " open" : ""}`}
                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke={open ? "#910E11" : "#9ca3af"} strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>

            {value && !viewMode && !isDisabled  && !errorMessage && (
                <span
                    className="date-field-clear"
                    onMouseDown={handleClear}
                >
                    ×
                </span>
            )}
            </div>

            {isTyping && errorMessage && (
                <span className="date-field-placeholder error">
                    {errorMessage}
                </span>
            )}

            {open && (
                <div className={`date-picker-dropdown${openUpward ? " open-upward" : ""}`}>
                    <Calendar
                        value={value instanceof Date ? value : null}
                        onChange={(date) => {
                            justSelectedRef.current = true;
                            onChange(field.name, date);
                            if (clearError) clearError(field.name);
                            if (onBlur) onBlur(field.name, date);
                            setIsTyping(false);
                            setDd(""); setMm(""); setYyyy("");
                            setOpen(false);
                        }}
                        defaultView="day"
                        minDate={minDate}
                        maxDate={maxDate}
                        clearError={clearError}
                    />
                </div>
            )}
        </div>
    );
}